import { GU } from 'components/common/graphicUploader/types';
import {
  createInspectionTemplateGeneralNormDocumentsBaseUrl,
  createTemplateChecklistItemDocumentsBaseUrl,
  createTemplateChecklistSectionDocumentsBaseUrl,
} from 'shared/domain/template/templateDocumentPaths';
import { logError } from 'setup/logger/logError';
import { FormChecklistItem, FormSection } from 'views/template/wizard/form/types';

interface IdentifiableUploader {
  _id: string;
  graphicUploader: GU;
}

interface SectionUploader {
  _id: string;
  graphicUploader: GU;
}

export function buildIdentifiableUploaders(
  checklist: readonly FormChecklistItem[]
): IdentifiableUploader[] {
  return checklist
    .map(
      (item) =>
        item._id && {
          _id: item._id,
          graphicUploader: item.graphicUploader,
        }
    )
    .filter((uploader): uploader is IdentifiableUploader => Boolean(uploader));
}

export function buildSectionUploaders(
  sections: readonly FormSection[],
  createdSectionIdentifierByKey: ReadonlyMap<string, string>
): SectionUploader[] {
  return sections
    .map((section) => {
      const mappedSectionId =
        section._id || createdSectionIdentifierByKey.get(section.uniqueKeyId);

      if (!section.graphicUploader || !mappedSectionId) {
        return null;
      }

      return {
        _id: mappedSectionId,
        graphicUploader: section.graphicUploader,
      };
    })
    .filter((sectionUploader): sectionUploader is SectionUploader => Boolean(sectionUploader));
}

export function uploadCreatedChecklistItemFiles(
  templateId: string,
  checklist: readonly FormChecklistItem[],
  createdChecklistItems: ReadonlyArray<{ _id: string }>
): Promise<unknown[]> {
  return Promise.all(
    createdChecklistItems.map((checklistItemResponse, index) => {
      return checklist[index].graphicUploader?.uploadFiles(
        createTemplateChecklistItemDocumentsBaseUrl(
          templateId,
          checklistItemResponse._id
        ),
        () => Promise.resolve()
      );
    })
  );
}

export function startTemplateUpdateBackgroundSync(
  templateId: string,
  uploader: GU,
  checklist: readonly FormChecklistItem[],
  sections: readonly FormSection[],
  createdSectionIdentifierByKey: ReadonlyMap<string, string>
): void {
  const identifiableUploaders = buildIdentifiableUploaders(checklist);
  const sectionUploaders = buildSectionUploaders(
    sections,
    createdSectionIdentifierByKey
  );

  void (async () => {
    try {
      await Promise.all([
        uploader.uploadFiles(
          createInspectionTemplateGeneralNormDocumentsBaseUrl(templateId),
          () => Promise.resolve()
        ),
        ...identifiableUploaders.map((identifiableUploader) => {
          return identifiableUploader.graphicUploader.uploadFiles(
            createTemplateChecklistItemDocumentsBaseUrl(
              templateId,
              identifiableUploader._id
            ),
            () => Promise.resolve()
          );
        }),
        ...sectionUploaders.map((sectionUploader) => {
          return sectionUploader.graphicUploader.uploadFiles(
            createTemplateChecklistSectionDocumentsBaseUrl(
              templateId,
              sectionUploader._id
            ),
            () => Promise.resolve()
          );
        }),
      ]);

      await Promise.all([
        ...uploader.executeChangeset(),
        ...identifiableUploaders.flatMap((identifiableUploader) =>
          identifiableUploader.graphicUploader.executeChangeset()
        ),
        ...sectionUploaders.flatMap((sectionUploader) =>
          sectionUploader.graphicUploader.executeChangeset()
        ),
      ]);
    } catch (error) {
      logError('Background file sync after template update', error);
    }
  })();
}

export function startTemplateCreateBackgroundUploads(
  templateId: string,
  uploader: GU,
  checklist: readonly FormChecklistItem[],
  createdChecklistItems: ReadonlyArray<{ _id: string }>,
  sectionUploaders: readonly SectionUploader[]
): void {
  void (async () => {
    try {
      await uploader.uploadFiles(
        createInspectionTemplateGeneralNormDocumentsBaseUrl(templateId),
        () => Promise.resolve()
      );
      await uploadCreatedChecklistItemFiles(
        templateId,
        checklist,
        createdChecklistItems
      );
      await Promise.all(
        sectionUploaders.map((sectionUploader) =>
          sectionUploader.graphicUploader.uploadFiles(
            createTemplateChecklistSectionDocumentsBaseUrl(
              templateId,
              sectionUploader._id
            ),
            () => Promise.resolve()
          )
        )
      );
    } catch (error) {
      logError('Background file uploads after template create', error);
    }
  })();
}